import type {NotificationFeedQueryParams, NotificationItem, NotificationTopic, NotificationType} from "../types";

/** Topics loaded in the communications bell and reconnect hydrate. */
export const COMMUNICATIONS_BELL_TOPICS = [
  "stored_vehicle",
  "shift",
  "panic",
  "direct_message",
  "briefing_board",
] as const satisfies readonly NotificationTopic[];

/** Resident home bell — DMs + household updates only. */
export const RESIDENT_BELL_TOPICS = [
  "direct_message",
  "household",
] as const satisfies readonly NotificationTopic[];

/** Shift / vehicle / briefing alerts — excludes DMs (DM badge uses sidebar Messages). */
export const ALERTS_BELL_TOPICS = [
  "stored_vehicle",
  "shift",
  "panic",
  "briefing_board",
] as const satisfies readonly NotificationTopic[];

/** @deprecated Use COMMUNICATIONS_BELL_TOPICS */
export const NOTIFICATION_FEED_TOPICS = COMMUNICATIONS_BELL_TOPICS;

export type NotificationFeedRealtimeTopic =
  | (typeof COMMUNICATIONS_BELL_TOPICS)[number]
  | (typeof RESIDENT_BELL_TOPICS)[number];

export const COMMUNICATIONS_BELL_QUERY_PARAMS: NotificationFeedQueryParams = {
  unread: true,
  include_discarded: false,
  per_page: 25,
  topic: [...COMMUNICATIONS_BELL_TOPICS],
};

export const RESIDENT_BELL_QUERY_PARAMS: NotificationFeedQueryParams = {
  unread: true,
  include_discarded: false,
  per_page: 25,
  topic: [...RESIDENT_BELL_TOPICS],
};

export function getNotificationBellQueryParams(
  isResident: boolean,
): NotificationFeedQueryParams {
  return isResident ? RESIDENT_BELL_QUERY_PARAMS : COMMUNICATIONS_BELL_QUERY_PARAMS;
}

export function getNotificationBellTopics(isResident: boolean): NotificationTopic[] {
  return isResident ? [...RESIDENT_BELL_TOPICS] : [...COMMUNICATIONS_BELL_TOPICS];
}

const REALTIME_TYPES = new Set<NotificationType>([
  "stored_vehicle.tracking_active",
  "stored_vehicle.cite_eligible",
  "shift.break_alert",
  "shift.end_alert",
  "direct_message.post",
  "briefing_board.created",
  "briefing_board.ack_required",
  "panic.alert",
]);

/** Backend may still emit compliance.* aliases on some broadcast paths. */
const REALTIME_TYPE_ALIASES: Record<string, NotificationType> = {
  "compliance.break-notice": "shift.break_alert",
  "compliance.shift-end": "shift.end_alert",
};

export function isCommunicationsBellTopic(topic: string): topic is NotificationTopic {
  return (COMMUNICATIONS_BELL_TOPICS as readonly string[]).includes(topic);
}

export function isResidentBellTopic(topic: string): topic is NotificationTopic {
  return (RESIDENT_BELL_TOPICS as readonly string[]).includes(topic);
}

export function isAlertsBellTopic(topic: string): topic is NotificationTopic {
  return (ALERTS_BELL_TOPICS as readonly string[]).includes(topic);
}

export function isNotificationFeedRealtimeTopic(topic: string): topic is NotificationFeedRealtimeTopic {
  return isCommunicationsBellTopic(topic) || isResidentBellTopic(topic);
}

export function coerceNotificationFeedRealtimeTopic(topic: string | null): NotificationFeedRealtimeTopic | null {
  if (!topic) {
    return null;
  }
  if (topic === "compliance") {
    return "shift";
  }
  return isNotificationFeedRealtimeTopic(topic) ? topic : null;
}

export function isNotificationFeedRealtimeType(type: string): type is NotificationType {
  return REALTIME_TYPES.has(type as NotificationType);
}

export function coerceNotificationFeedRealtimeType(type: string | null): NotificationType | null {
  if (!type) {
    return null;
  }
  if (isNotificationFeedRealtimeType(type)) {
    return type;
  }
  if (type.startsWith("household.")) {
    return type as NotificationType;
  }
  return REALTIME_TYPE_ALIASES[type] ?? null;
}

export function isUrgentNotificationType(type: NotificationType): boolean {
  return (
    type === "shift.break_alert" ||
    type === "shift.end_alert" ||
    type === "stored_vehicle.cite_eligible" ||
    type === "panic.alert"
  );
}

export function isShiftNotificationTopic(topic: NotificationTopic): boolean {
  return topic === "shift";
}

/** Opening the thread (GET) or tap-to-open acks DM/BB rows — hide feed mark-read button; tap still calls mark-read for DM. */
export function isTopicAckedBySubjectOpen(topic: NotificationTopic): boolean {
  return topic === "direct_message" || topic === "briefing_board";
}

/** Impersonators must not see shift compliance rows in bell, badge, or /notifications. */
export function filterNotificationItemsForViewer(
  items: NotificationItem[],
  viewingAsUser: boolean,
): NotificationItem[] {
  if (!viewingAsUser) {
    return items;
  }
  return items.filter((item) => !isShiftNotificationTopic(item.topic));
}
